'use client';

import { c as _c } from "react/compiler-runtime";
import { BorderBeam, type BorderBeamPalette } from 'border-beam';
import { m, useInView, useReducedMotion } from 'motion/react';
import { type PointerEvent, type ReactNode, useRef, useState } from 'react';
import { outcomeHueToVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { EASE_POSITION_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { OutcomeRowSummary } from './outcome-row-summary';

// Two beam cycles give the background a slower drift than the border.
const GLOW_DRIFT = {
  duration: 4.6,
  ease: EASE_POSITION_MOTION,
  repeat: Infinity,
  repeatType: 'mirror'
} as const;
export function MarketOutcomeHover(t0) {
  const $ = _c(59);
  const {
    outcomes,
    winnerOriginalIndex,
    children
  } = t0;
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref);
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== outcomes) {
    t1 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colors = useOutcomeColor(t1);
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t2;
  let t20;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== children || $[3] !== colors || $[4] !== hovered || $[5] !== inView || $[6] !== outcomes || $[7] !== reduceMotion || $[8] !== winnerOriginalIndex) {
    t20 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t21;
      if ($[29] !== colors) {
        t21 = (o_0, i) => [o_0.originalIndex, colors[i]];
        $[29] = colors;
        $[30] = t21;
      } else {
        t21 = $[30];
      }
      const colorByIndex = new Map(outcomes.map(t21));
      const visible = [...outcomes].sort(_temp2).slice(0, 2).map(outcome => {
        const color = colorByIndex.get(outcome.originalIndex);
        const winner = winnerOriginalIndex === outcome.originalIndex;
        const resolved = winnerOriginalIndex !== undefined;
        return {
          index: outcome.originalIndex,
          ink: resolved ? winner ? "var(--color-feedback-success-content)" : "var(--color-background-tertiary)" : color === undefined ? undefined : outcomeHueToVar(color.hue, "text"),
          fill: resolved ? winner ? "bg-feedback-success-content" : "bg-background-tertiary" : cn("bg-current", color?.text)
        };
      });
      const first = visible[0]?.ink;
      const second = visible[1]?.ink;
      if (first === undefined || second === undefined) {
        t20 = children;
        break bb0;
      }
      const moving = hovered && inView && !reduceMotion;
      T0 = BorderBeam;
      t6 = ref;
      t7 = "pulse-inner";
      t8 = hoverPalette(first, second);
      t9 = true;
      t10 = "auto";
      t11 = 20;
      t12 = hovered && !reduceMotion;
      t13 = 1;
      t14 = 1;
      t15 = "relative h-full rounded-card";
      t16 = "market-outcome-hover";
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = event => {
          if (event.pointerType === "mouse") {
            setHovered(true);
          }
        };
        t18 = () => setHovered(false);
        $[31] = t17;
        $[32] = t18;
      } else {
        t17 = $[31];
        t18 = $[32];
      }
      t19 = children;
      t2 = "true";
      t3 = "market-outcome-glow";
      const t22 = hovered ? "opacity-15" : "opacity-0";
      if ($[33] !== t22) {
        t4 = cn("pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-card transition-opacity duration-slow ease-default motion-reduce:transition-none", t22);
        $[33] = t22;
        $[34] = t4;
      } else {
        t4 = $[34];
      }
      let t23;
      if ($[35] !== moving) {
        t23 = (color_0, index) => <m.div key={color_0.index} data-outcome-glow={color_0.index} className={cn("absolute h-full w-3/4 rounded-full blur-3xl", index === 0 ? "-left-8 -top-8" : "-right-8 -bottom-8", color_0.fill)} initial={false} animate={moving ? {
          x: index === 0 ? [0, 24] : [0, -24],
          y: [0, 16],
          scale: [1, 1.15]
        } : {
          x: 0,
          y: 0,
          scale: 1
        }} transition={moving ? GLOW_DRIFT : {
          duration: 0
        }} />;
        $[35] = moving;
        $[36] = t23;
      } else {
        t23 = $[36];
      }
      t5 = visible.map(t23);
    }
    $[2] = children;
    $[3] = colors;
    $[4] = hovered;
    $[5] = inView;
    $[6] = outcomes;
    $[7] = reduceMotion;
    $[8] = winnerOriginalIndex;
    $[9] = T0;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
    $[18] = t18;
    $[19] = t19;
    $[20] = t2;
    $[21] = t20;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
    t18 = $[18];
    t19 = $[19];
    t2 = $[20];
    t20 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  if (t20 !== Symbol.for("react.early_return_sentinel")) {
    return t20;
  }
  let t21;
  if ($[37] !== t2 || $[38] !== t3 || $[39] !== t4 || $[40] !== t5) {
    t21 = <div aria-hidden={t2} data-testid={t3} className={t4}>{t5}</div>;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t5;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t15 || $[49] !== t16 || $[50] !== t17 || $[51] !== t18 || $[52] !== t19 || $[53] !== t21 || $[54] !== t6 || $[55] !== t7 || $[56] !== t8 || $[57] !== t9) {
    t22 = <T0 ref={t6} size={t7} colorVariant={t8} staticColors={t9} theme={t10} borderRadius={t11} active={t12} brightness={t13} saturation={t14} className={t15} data-testid={t16} onPointerEnter={t17} onPointerLeave={t18}>{t19}{t21}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
    $[53] = t21;
    $[54] = t6;
    $[55] = t7;
    $[56] = t8;
    $[57] = t9;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}
function _temp2(a, b) {
  return b.share - a.share || a.originalIndex - b.originalIndex;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.originalIndex
  };
}
function hoverPalette(first: string, second: string): BorderBeamPalette {
  return {
    border: [{
      color: first,
      pos: '33% -7.4%',
      size: '70px 40px'
    }, {
      color: first,
      pos: '12% -5%',
      size: '60px 35px'
    }, {
      color: first,
      pos: '2.1% 68.3%',
      size: '40px 70px'
    }, {
      color: first,
      pos: '2.1% 68.3%',
      size: '20px 35px'
    }, {
      color: second,
      pos: '74.4% 100%',
      size: '180px 32px'
    }, {
      color: second,
      pos: '55% 100%',
      size: '85px 26px'
    }, {
      color: second,
      pos: '93.9% 0%',
      size: '74px 32px'
    }, {
      color: second,
      pos: '100% 27.1%',
      size: '26px 42px'
    }, {
      color: second,
      pos: '100% 27.1%',
      size: '52px 48px'
    }]
  };
}